/**
 * Landmarks + blendshapes -> MouthSample.
 *
 * Deliberately free of MediaPipe, the DOM and the clock: it takes plain arrays
 * and returns a plain object, which is what lets the whole of Phase 1's maths be
 * tested against synthetic faces rather than only judged by squinting at meters.
 * `landmarker.js` owns everything that needs a camera.
 *
 * Two things here are not in the spec and are not optional:
 *
 * **Aspect correction.** MediaPipe normalises x by image *width* and y by image
 * *height*, independently. On a 16:9 frame that makes one pixel step in x worth
 * a different number than the same step in y, so a Euclidean distance across
 * those coordinates is stretched. It does not cancel when you divide by the
 * inter-ocular distance either, because `iod` is horizontal and the lip gap is
 * vertical — the exact pair the spec divides one by the other. Left alone, the
 * same face reads a different aperture on a 4:3 webcam than on a 16:9 one. Every
 * landmark is converted to a square space before any distance is taken.
 *
 * **Head-motion removal in lipJitter.** The upper lip's raw y moves whenever the
 * head does, and a nod produces far more variance than a trill. The jitter is
 * measured relative to the eye midpoint and scaled by iod, so it responds to the
 * lip moving against the face rather than the face moving against the camera.
 */

import { config } from '../core/config.js';

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Landmark to a square coordinate space, in units of image height.
 * @param {{x: number, y: number}} p
 * @param {number} aspect image width / height
 */
const square = (p, aspect) => ({ x: p.x * aspect, y: p.y });

/**
 * @param {{x: number, y: number}} a
 * @param {{x: number, y: number}} b
 */
function dist(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Blendshape categories -> a name-keyed score map.
 *
 * @param {{categoryName: string, score: number}[]} categories
 * @returns {Map<string, number>}
 */
function scoreMap(categories) {
  const m = new Map();
  for (const c of categories) m.set(c.categoryName, c.score);
  return m;
}

export class MouthFeatures {
  constructor() {
    /** Adaptive width calibration. Null until the first face. @type {?{min: number, max: number}} */
    this._width = null;
    /** Recent upper-lip offsets, for the jitter variance. @type {number[]} */
    this._lipY = [];
    /**
     * Blendshape names the model did not supply. Populated once, surfaced by the
     * readout — a wrong name here reads as a muscle that never moves, which is
     * indistinguishable from a face that never moves it.
     * @type {string[]}
     */
    this.missingShapes = [];
    this._checkedShapes = false;
  }

  /** Drop all calibration. Use when the camera or the face changes. */
  reset() {
    this._width = null;
    this._lipY = [];
  }

  /** Current adaptive width range, for the readout. */
  get calibration() {
    if (!this._width) return { min: 0, max: 0, span: 0, ready: false };
    const span = this._width.max - this._width.min;
    return { ...this._width, span, ready: span >= config.vision.WIDTH_MIN_SPAN };
  }

  /**
   * @param {{x: number, y: number, z?: number}[]} landmarks 478 normalized face landmarks
   * @param {{categoryName: string, score: number}[]} categories blendshape scores
   * @param {number} t performance.now() at frame capture
   * @param {number} aspect video width / height
   * @returns {import('../core/types.js').MouthSample|null} null if there is no usable face
   */
  update(landmarks, categories, t, aspect) {
    const { idx, shapes } = config.vision;
    if (!landmarks || landmarks.length <= idx.eyeR) return null;

    const scores = scoreMap(categories ?? []);
    if (!this._checkedShapes) {
      this._checkedShapes = true;
      this.missingShapes = Object.values(shapes)
        .flat()
        .filter((n) => !scores.has(n));
    }

    /** Mean of a left/right pair, or the single value. */
    const shape = (key) => {
      const names = shapes[key];
      let sum = 0;
      for (const n of names) sum += scores.get(n) ?? 0;
      return sum / names.length;
    };

    const p = (i) => square(landmarks[i], aspect);

    // Everything divides by this, so a face at arm's length and a face filling
    // the frame produce the same numbers.
    const iod = dist(p(idx.eyeL), p(idx.eyeR));
    if (!(iod > 1e-6)) return null;

    const jawOpen = shape('jawOpen');
    const pucker = shape('pucker');
    const funnel = shape('funnel');
    const close = shape('close');
    const press = shape('press');
    const smile = shape('smile');
    const stretch = shape('stretch');
    const lowerDown = shape('lowerDown');

    // ── aperture ────────────────────────────────────────────────────────────
    // The blend is the point: jawOpen alone is noisy on wide vowels, and the
    // geometry alone drifts with head pitch, because a tilted face foreshortens
    // the vertical lip gap while the horizontal iod barely changes.
    // Both terms are normalised by the value each signal actually reaches on a
    // real face. `jawOpen` in particular does not span 0..1 — it tops out near
    // 0.55 — so using it raw gave aperture a ceiling of about 0.76 and left the
    // spec's 0.7 threshold sitting in the noise. See config.vision.JAW_FULL.
    const gap = dist(p(idx.lipUpperInner), p(idx.lipLowerInner)) / iod;
    const v = config.vision;
    const aperture = clamp01(
      v.APERTURE_JAW * (jawOpen / v.JAW_FULL) + v.APERTURE_GEOM * (gap / v.GAP_FULL),
    );

    // ── width ───────────────────────────────────────────────────────────────
    const corner = dist(p(idx.cornerL), p(idx.cornerR)) / iod;

    if (!this._width) {
      this._width = { min: corner, max: corner };
    } else {
      const w = this._width;
      // Expand instantly on a new extreme, contract slowly toward the current
      // reading. Without the contraction one wide grin sets a maximum that
      // nothing reaches again and every later smile reads as neutral.
      if (corner < w.min) w.min = corner;
      else w.min += (corner - w.min) * v.WIDTH_DECAY;
      if (corner > w.max) w.max = corner;
      else w.max += (corner - w.max) * v.WIDTH_DECAY;
    }

    const span = this._width.max - this._width.min;
    const calibrated = span >= v.WIDTH_MIN_SPAN;
    const widthGeom = calibrated ? clamp01((corner - this._width.min) / span) : 0.5;

    // Blendshapes know wide from narrow before any range has been observed, so
    // they carry width alone until the geometry has something to normalise
    // against. Smile and stretch widen; pucker and funnel narrow.
    const widthShape = clamp01(
      0.5 + v.WIDTH_SHAPE_GAIN * ((smile + stretch) / 2 - (pucker + funnel) / 2),
    );
    const width = calibrated
      ? v.WIDTH_GEOM_MIX * widthGeom + (1 - v.WIDTH_GEOM_MIX) * widthShape
      : widthShape;

    // ── lipJitter ───────────────────────────────────────────────────────────
    // Upper lip against the eye line, in iod units, so a nodding head does not
    // read as a trill.
    //
    // The measure is the mean square SECOND difference, not the variance of the
    // window. That change came out of the first real recording: variance over
    // six frames saturated on every sound tested — "ah", "eee", "oo", "papa",
    // "meep" and a trill all pegged at 1.0 — because six frames at the ~18 Hz
    // this camera actually delivers is a third of a second, and a third of a
    // second of any speech moves the lip a long way. Variance was measuring
    // "the mouth is doing something", which is not a cue for anything.
    //
    // A second difference is near zero for smooth motion however large — an
    // opening jaw is a ramp, and a ramp has no curvature — and large for
    // frame-to-frame reversal. A trill aliases badly at this frame rate (25 Hz
    // against a Nyquist of 9), but aliasing is exactly what produces
    // incoherent frame-to-frame reversal, so it survives as elevated curvature
    // even though the rate does not. Phase 6 only needs presence; the audio
    // already has the rate.
    const eyeMidY = (p(idx.eyeL).y + p(idx.eyeR).y) / 2;
    const lipOffset = (p(idx.lipUpperOuter).y - eyeMidY) / iod;
    this._lipY.push(lipOffset);
    while (this._lipY.length > v.JITTER_WINDOW) this._lipY.shift();

    let jitterRaw = 0;
    const y = this._lipY;
    if (y.length >= 3) {
      let sum = 0;
      for (let i = 2; i < y.length; i++) {
        const d2 = y[i] - 2 * y[i - 1] + y[i - 2];
        sum += d2 * d2;
      }
      jitterRaw = sum / (y.length - 2);
    }
    const lipJitter = clamp01(jitterRaw / v.JITTER_FULL);

    return {
      t,
      aperture,
      width,
      jawOpen,
      pucker,
      funnel,
      close,
      press,
      smile,
      stretch,
      lowerDown,
      lipJitter,
      // Not part of the contract — carried for the readout and for retuning.
      // `lipOffset` is here so JITTER_FULL can be fitted from a recording
      // offline: any jitter measure at all can be recomputed from the raw
      // offsets, whereas none of them can be recovered from lipJitter once it
      // has already been squashed into 0..1 and clamped.
      _raw: { iod, gap, corner, lipOffset, jitterRaw, widthGeom, widthShape, calibrated },
    };
  }
}

export default MouthFeatures;
